/**
 * StructureConsoleModule — THE STRUCTURE CONSOLE (2026-09-23).
 *
 * Inside every player-placed structure a console stands against the back wall (the generated
 * `structure:city_console` model, unlit like every interior surface; a slab with a teal screen
 * when the model is not registered). Within CONSOLE_USE_M a prompt offers E; the window it opens
 * manages the structure from where its owner stands:
 *   · NAME — what the door prompt calls it (`setStructureName`);
 *   · ENTRY — public (anyone not banned) or private (owner, admins, the entry list)
 *     (`setStructureEntry`), and the ACCESS LIST by username (`setStructurePermission` /
 *     `removeStructurePermission`);
 *   · MAINTENANCE — the pool, the condition, a payment (`payMaintenance`);
 *   · REDEED — pack the structure back into a deed in the inventory (`redeedStructure`, the
 *     server's two-step: arm, then confirm within 30 s); the interior exits as the exterior goes.
 * The server judges every call (owner / admin); the window only shows what the rows say.
 * The console follows `atlas.interiorId` (set by BuildingInteriorModule on enter, cleared on exit).
 */

import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { keyIs } from '../lib/keybinds.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';
import { buildRentalsSection } from './rentals/console_rentals.js'; // RENTALS (the interiors programme, slice R, 2026-10-01)
import { enterables, interiorSpec } from '../lib/building_interiors.js';
import { consoleSeat, CONSOLE_HEIGHT_M, CONSOLE_USE_M, structureDisplayName, type PermLevel } from '../lib/structure_access.js';
import { STRUCTURE_LABELS } from '@shared/types/structures.js';
import { FURNISH_PACKS, packItemCounts, packPlan, furnishPack, type FurnishPack } from '../lib/furnish_packs.js';
import { resolvePurpose } from '../lib/interior_purpose.js';
import { FURNITURE } from '../lib/furniture_catalog.js';
import {
  activeInterior, interiorPlannerEnabled, packCountsOf, planInputFor, planCachePeek, unseatedPackKeys,
  CURRENT_LAYOUT_REVISION, LAYOUT_PIN_FLAG, type LayoutPin,
} from '../lib/interior_wiring.js';
import { planRoom } from './interior/interior_plan_client.js';
import { STOREYS_FLAG } from '../lib/interior_rooms.js'; // (slice H for slice U) the shard's current layout follows the storeys flag
import { ROOMS_LAYOUT_REVISION } from '../lib/interior_descriptor.js';
import type { FurnitureUseModule } from './FurnitureUseModule.js';
import { markShared, deferDisposeTree } from '../lib/defer_dispose.js';
import { interactFocus } from '../lib/interact_focus.js';
import { sfx } from '../lib/sfx.js';
import type { AssetModule } from './AssetModule.js';
import type { PhysicsModule } from './PhysicsModule.js';

const COLLIDER_KEY = 'int:structure-console';
const REDEED_ARM_MS = 30_000;

interface Placed {
  interiorId: string;
  structureId: bigint;
  group: THREE.Group;
  /** Absolute world position of the console's foot. */
  x: number; y: number; z: number;
  release: (() => void) | null;
  mats: THREE.Material[];
  gen: number;
}

interface StructureRow { structureId: bigint; owner: Identity; structureType: string; maintPool: bigint; condition: number; itemCount: number; itemCap: number; cityId: bigint; lotsUsed: number; redeedPendingAt: { microsSinceUnixEpoch: bigint } }
interface MetaRow { structureId: bigint; name: string; entry: string }
interface FurnishRow { structureId: bigint; pack: number; paidSov: bigint }
interface PermRow { permId: bigint; structureId: bigint; subject: Identity; level: string }
interface AccountRow { identity: Identity; username: string; displayName: string }

export class StructureConsoleModule implements IModule {
  readonly name = 'StructureConsole';

  private scene!: THREE.Scene;
  private app!: App;
  private placed: Placed | null = null;
  /** The active interior's version the open window last rendered (a plan landing re-renders it). */
  private renderedVersion = -1;
  private gen = 0;
  private prompt: HTMLElement | null = null;
  private near = false;
  private win: UiWindow | null = null;
  private winFor: bigint | null = null;
  private armedAt = 0;
  private renderTimer: number | null = null;
  private onKey = (e: KeyboardEvent): void => this.key(e);

  constructor(private readonly assets: AssetModule, private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    const p = document.createElement('div');
    p.id = 'structure-console-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    p.textContent = 'E — structure console';
    document.body.appendChild(p);
    this.prompt = p;
    window.addEventListener('keydown', this.onKey);
    bus.on('atlas:place-requested', () => this.press()); // (slice H) the pad's interact opens the console
    app.world.addSystem(makeSystem('StructureConsole', Phase.UI, 36, () => this.tick()));
    bus.on('atlas:world-cleared', () => { this.teardown(); this.win?.setOpen(false); });
    // Re-render the open window as its rows move.
    const conn = atlas.conn;
    if (conn) this.watchRows();
    bus.on('atlas:account-ready', () => this.watchRows());
    (window as unknown as { __structureConsole?: unknown }).__structureConsole = () => this.diag();
  }

  private watched = false;
  private watchRows(): void {
    const conn = atlas.conn;
    if (!conn || this.watched) return;
    this.watched = true;
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    // A furnish row changing under the player re-seats the room (the interior module listens).
    const fz = db.structureFurnish as { onInsert?(cb: (c: unknown, r: FurnishRow) => void): void; onUpdate?(cb: (c: unknown, o: FurnishRow, r: FurnishRow) => void): void; onDelete?(cb: (c: unknown, r: FurnishRow) => void): void } | undefined;
    const refurnish = (id: bigint): void => { if (this.placed && this.placed.structureId === id) bus.emit('atlas:interior-refurnish', { structureId: id.toString() }); };
    fz?.onInsert?.((_c, r) => refurnish(r.structureId));
    fz?.onUpdate?.((_c, _o, r) => refurnish(r.structureId));
    fz?.onDelete?.((_c, r) => refurnish(r.structureId));
    // + citizen / civicStructure (2026-09-25): the redeed's city rule reads them (a residence declared elsewhere frees it).
    // + the layout pin and the pack storage (slice C): the Furnishing section shows the pin, the Refit guard and what a layout left unseated.
    for (const name of ['structure', 'structureMeta', 'structurePermission', 'structureDeed', 'structureFurnish', 'citizen', 'civicStructure', 'structureLayout', 'structureContainer', 'containerItem', 'structurePlanter', 'rentalUnit', 'rentalLease', 'rentalReceipt']) {
      const t = db[name];
      if (!t) continue;
      const ping = (): void => this.scheduleRender();
      t.onInsert?.(ping); t.onUpdate?.(ping); t.onDelete?.(ping);
    }
  }

  // ─── The console in the room ────────────────────────────────────────────────

  private tick(): void {
    const id = atlas.interiorId;
    if (this.placed && this.placed.interiorId !== id) this.teardown();
    if (!this.placed && id && id.startsWith('deed:')) this.setup(id);
    if (!this.placed) { this.showPrompt(false); this.near = false; if (this.win?.isOpen()) this.win.setOpen(false); return; }
    const d = Math.hypot(atlas.localX - this.placed.x, atlas.localZ - this.placed.z);
    this.near = d <= CONSOLE_USE_M;
    const actV = activeInterior()?.version ?? -1; // the room's plan / pin moved: the Furnishing section reads it
    if (actV !== this.renderedVersion) { this.renderedVersion = actV; this.scheduleRender(); }
    // THE CONSOLE'S ZONE: within CONSOLE_USE_M the E key is the console's — the door module yields its own
    // prompt while this one shows (a small house's door is only ~5 m from the back wall, inside the door's
    // 4.5 m range; without the yield E at the console would walk you out).
    const focus = interactFocus();
    if (this.near) focus.claim({ id: 'console', dist: d, priority: 1, label: 'console' });
    this.showPrompt(this.near && focus.owns('console') && !this.win?.isOpen());
  }

  private showPrompt(on: boolean): void {
    if (!this.prompt) return;
    const cur = this.prompt.style.display !== 'none';
    if (cur !== on) this.prompt.style.display = on ? 'block' : 'none';
  }

  private setup(interiorId: string): void {
    const b = enterables.get(interiorId);
    if (!b) return;
    const structureId = BigInt(interiorId.slice('deed:'.length));
    const spec = interiorSpec(b.w, b.h, b.d, b.key);
    const seat = consoleSeat(spec);
    const cos = Math.cos(b.yaw), sin = Math.sin(b.yaw);
    const x = b.x + seat.x * cos + seat.z * sin, z = b.z - seat.x * sin + seat.z * cos;
    const y = b.groundY + 0.08; // the floor slab's top (the furnisher's floor-0 height)
    const group = new THREE.Group();
    group.name = 'structure-console';
    group.position.set(x, y, z); // a scene child is ABSOLUTE (the frame law)
    group.rotation.y = b.yaw + seat.yaw;
    this.scene.add(group);
    const gen = ++this.gen;
    const placed: Placed = { interiorId, structureId, group, x, y, z, release: null, mats: [], gen };
    this.placed = placed;
    this.physics.addStaticCollider(COLLIDER_KEY, { shape: 'box', halfExtents: [0.45, CONSOLE_HEIGHT_M / 2, 0.35] }, { x, y: y + CONSOLE_HEIGHT_M / 2, z, yaw: b.yaw + seat.yaw, scale: 1 });
    // A stand-in slab first (always something to walk up to), the generated console over it when it lands.
    const slab = this.buildSlab(placed);
    group.add(slab);
    void this.dress(placed, slab);
  }

  /** The generated `structure:city_console` model, unlit, fitted to CONSOLE_HEIGHT_M. */
  private async dress(placed: Placed, slab: THREE.Object3D): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    let assetId: bigint | null = null;
    for (const pm of conn.db.prefabModel.iter()) if (pm.prefabKey === 'structure:city_console') { assetId = pm.assetId; break; }
    if (assetId === null) return;
    try {
      const { object: master, release } = await this.assets.loadForTier(assetId);
      if (this.placed !== placed || placed.gen !== this.gen) { release(); return; }
      placed.release = release;
      const bb = new THREE.Box3().setFromObject(master);
      const h = Math.max(0.01, bb.max.y - bb.min.y);
      const scale = CONSOLE_HEIGHT_M / h;
      const clone = master.clone(true);
      markShared(clone); // its geometry is the asset cache's — never disposed here
      clone.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if ((mesh as { isMesh?: boolean }).isMesh && mesh.material) {
          const src = mesh.material as THREE.MeshStandardMaterial;
          const basic = new THREE.MeshBasicMaterial({ map: src.map ?? null, color: 0xffffff }); // the interior-materials law: unlit
          mesh.material = basic;
          placed.mats.push(basic);
        }
      });
      // Seat by the bbox bottom-centre (the PIVOT law) at the group's origin.
      clone.position.set(-(bb.min.x + bb.max.x) / 2 * scale, -bb.min.y * scale, -(bb.min.z + bb.max.z) / 2 * scale);
      clone.scale.setScalar(scale);
      placed.group.add(clone);
      slab.visible = false;
    } catch { /* the slab stays */ }
  }

  private buildSlab(placed: Placed): THREE.Object3D {
    const g = new THREE.Group();
    const body = new THREE.MeshBasicMaterial({ color: 0x2a3140 });
    const screen = new THREE.MeshBasicMaterial({ color: 0x3fd0c8 });
    placed.mats.push(body, screen);
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.6, CONSOLE_HEIGHT_M, 0.35), body);
    post.position.y = CONSOLE_HEIGHT_M / 2;
    const face = new THREE.Mesh(new THREE.PlaneGeometry(0.46, 0.34), screen);
    face.position.set(0, CONSOLE_HEIGHT_M - 0.3, 0.18); // the screen on the group's +Z, which faces the room after the seat's yaw
    g.add(post, face);
    return g;
  }

  private teardown(): void {
    const p = this.placed;
    if (!p) return;
    this.placed = null;
    this.gen++;
    this.scene.remove(p.group);
    deferDisposeTree(p.group); // the shared clone is skipped (markShared); the slab's own geometry is freed
    const mats = p.mats;
    setTimeout(() => { for (const m of mats) m.dispose(); }, 400);
    p.release?.();
    this.physics.removeStaticCollider(COLLIDER_KEY);
    this.showPrompt(false);
    if (this.win?.isOpen()) this.win.setOpen(false);
  }

  // ─── The window ─────────────────────────────────────────────────────────────

  private key(e: KeyboardEvent): void {
    if (!keyIs(e.code, 'interact') || e.ctrlKey || e.metaKey || e.repeat) return; // the BOUND key: a rebound E still opened it
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    this.press();
  }
  /** (slice H — item 5, a declared hunk) The E key and the interact ACTION (a pad's button, a rebound key) — one press. */
  private lastPress = 0;
  private press(): void {
    const now = performance.now();
    if (now - this.lastPress < 150) return;
    this.lastPress = now;
    if (!this.placed || !this.near) return;
    if (!interactFocus().owns('console')) return; // ONE KEY, ONE FOCUS
    if (this.win?.isOpen()) { this.win.setOpen(false); return; }
    this.open();
  }

  private open(): void {
    if (!this.placed) return;
    if (!this.win) {
      this.win = makeWindow({ id: 'structure-console', title: 'Structure Console', icon: 'build', width: 600, height: 560, minWidth: 460, minHeight: 360 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;font:13px var(--ax-font);color:var(--ax-text);overflow:auto;padding:10px 12px;gap:10px;';
    }
    this.winFor = this.placed.structureId;
    this.win.setOpen(true);
    sfx('sfx_notify', { gain: 0.2 });
    this.render();
  }

  private scheduleRender(): void {
    if (!this.win?.isOpen()) return;
    if (this.renderTimer !== null) return;
    this.renderTimer = window.setTimeout(() => { this.renderTimer = null; this.render(); }, 120);
  }

  private rows(of: bigint | null = this.winFor): { s: StructureRow | null; meta: MetaRow | null; furnish: FurnishRow | null; perms: PermRow[]; accounts: AccountRow[]; me: Identity | null } {
    const conn = atlas.conn;
    const id = of;
    if (!conn || id === null) return { s: null, meta: null, furnish: null, perms: [], accounts: [], me: null };
    const db = conn.db as unknown as {
      structure: { structureId: { find(id: bigint): StructureRow | undefined } };
      structureMeta?: { structureId: { find(id: bigint): MetaRow | undefined } };
      structureFurnish?: { structureId: { find(id: bigint): FurnishRow | undefined } };
      structurePermission: { iter(): Iterable<PermRow> };
      account: { iter(): Iterable<AccountRow> };
    };
    return {
      s: db.structure.structureId.find(id) ?? null,
      meta: db.structureMeta?.structureId.find(id) ?? null,
      furnish: db.structureFurnish?.structureId.find(id) ?? null,
      perms: [...db.structurePermission.iter()].filter((p) => p.structureId === id),
      accounts: [...db.account.iter()],
      me: conn.identity ?? null,
    };
  }

  /** SOFT RENDER (2026-09-25): the window re-rendered on every structure / meta / permission / deed / furnish row
   *  anyone touched, and `replaceChildren()` wiped a NAME being typed (and the caret and the scroll) each time. The
   *  fresh tree is patched over the live one: only what changed is replaced, never the focused field. */
  private render(): void {
    const win = this.win;
    if (!win) return;
    softRender(win.body, (body) => this.build(win, body));
  }

  private build(win: UiWindow, body: HTMLElement): void {
    const live = <T extends HTMLInputElement | HTMLSelectElement>(k: string, fallback: T): T => fieldByKey<T>(win.body, k) ?? fallback; // the field ON SCREEN (a kept one holds the typing)
    const { s, meta, furnish, perms, accounts, me } = this.rows();
    if (!s) { body.appendChild(this.text('This structure is gone.', 'var(--ax-bad)')); return; }
    const nameOf = (id: Identity): string => accounts.find((a) => a.identity.isEqual(id))?.username ?? id.toHexString().slice(0, 10) + '…';
    const isOwner = !!me && s.owner.isEqual(me);
    const myLevel = me ? perms.find((p) => p.subject.isEqual(me))?.level ?? null : null;
    const admin = isOwner || myLevel === 'owner' || myLevel === 'admin';
    const typeLabel = STRUCTURE_LABELS[s.structureType] ?? s.structureType.replace(/_/g, ' ');
    const shown = structureDisplayName(meta?.name, typeLabel);
    win.setTitle(`Structure Console · ${shown}`);
    const structureId = s.structureId;
    const call = (label: string, fn: () => Promise<unknown>): void => {
      fn().then(() => { bus.emit('atlas:toast', { text: label, kind: 'success', ms: 2500 }); this.scheduleRender(); })
        .catch((err: Error) => bus.emit('atlas:toast', { text: String(err?.message ?? err).replace(/^SenderError:\s*/, '').slice(0, 160), kind: 'error' }));
    };
    const r = atlas.conn!.reducers as unknown as {
      setStructureName(a: { structureId: bigint; name: string }): Promise<void>;
      setStructureEntry(a: { structureId: bigint; entry: string }): Promise<void>;
      setStructurePermission(a: { structureId: bigint; subject: Identity; level: string }): Promise<void>;
      removeStructurePermission(a: { structureId: bigint; subject: Identity }): Promise<void>;
      payMaintenance(a: { structureId: bigint; amount: bigint }): Promise<void>;
      redeedStructure(a: { structureId: bigint; confirm: boolean }): Promise<void>;
      buyFurnishPack(a: { structureId: bigint; pack: number }): Promise<void>;
      clearFurnishPack(a: { structureId: bigint }): Promise<void>;
    };

    // Header
    body.appendChild(this.heading(`${typeLabel} · #${structureId} · owner ${nameOf(s.owner)}${admin ? '' : ' · view only'}`));
    if (!admin) body.appendChild(this.text('You may enter this structure; only its owner and admins manage it.', 'var(--ax-muted)'));

    // NAME
    const nameSec = this.section('NAME — what the door calls it');
    const nameIn = this.input(meta?.name ?? '', typeLabel, 40);
    nameIn.disabled = !admin;
    nameIn.setAttribute('data-k', 'name');
    const nameBtn = this.button('Save name', () => call('Name saved.', () => r.setStructureName({ structureId, name: live('name', nameIn).value })), admin);
    nameSec.appendChild(this.row(nameIn, nameBtn));
    body.appendChild(nameSec);

    // ENTRY
    const entrySec = this.section('ENTRY — who may walk in');
    const entry = meta?.entry === 'private' ? 'private' : 'public';
    const sel = this.select([['public', 'Public — anyone not banned'], ['private', 'Private — owner, admins and the entry list']], entry);
    sel.disabled = !admin;
    sel.setAttribute('data-k', 'entry');
    sel.addEventListener('change', () => call(`Entry set to ${sel.value}.`, () => r.setStructureEntry({ structureId, entry: sel.value })));
    entrySec.appendChild(this.row(sel));
    body.appendChild(entrySec);

    // ACCESS LIST
    const accSec = this.section('ACCESS LIST — admin manages · entry may enter a private door · banned never enters');
    if (perms.length === 0) accSec.appendChild(this.text('No entries yet.', 'var(--ax-muted)'));
    for (const p of perms) {
      const lvl = this.select([['admin', 'admin'], ['entry', 'entry'], ['banned', 'banned']], p.level);
      lvl.disabled = !admin;
      lvl.addEventListener('change', () => call(`${nameOf(p.subject)} → ${lvl.value}.`, () => r.setStructurePermission({ structureId, subject: p.subject, level: lvl.value })));
      const rm = this.button('Remove', () => call(`${nameOf(p.subject)} removed.`, () => r.removeStructurePermission({ structureId, subject: p.subject })), admin);
      const who = this.text(nameOf(p.subject), 'var(--ax-text)'); who.style.minWidth = '140px';
      who.setAttribute('data-perm', p.subject.toHexString());
      const permRow = this.row(who, lvl, rm);
      permRow.setAttribute('data-row', `perm:${p.subject.toHexString()}`); // rows pair by identity in the soft render
      accSec.appendChild(permRow);
    }
    const addIn = this.input('', 'username', 32);
    addIn.disabled = !admin;
    addIn.setAttribute('data-k', 'add-user');
    const addLvl = this.select([['entry', 'entry'], ['admin', 'admin'], ['banned', 'banned']], 'entry');
    addLvl.disabled = !admin;
    addLvl.setAttribute('data-k', 'add-level');
    const addBtn = this.button('Add player', () => {
      const typed = live('add-user', addIn).value.trim(), level = live('add-level', addLvl).value;
      const uname = typed.toLowerCase();
      const acc = accounts.find((a) => a.username.toLowerCase() === uname);
      if (!acc) { bus.emit('atlas:toast', { text: `No traveler named "${typed}"`, kind: 'error' }); return; }
      if (acc.identity.isEqual(s.owner)) { bus.emit('atlas:toast', { text: 'The owner needs no entry.', kind: 'info' }); return; }
      // Cleared once added: the soft render keeps a field's text, the old rebuild cleared it (2026-09-25).
      call(`${acc.username} added as ${level}.`, () => r.setStructurePermission({ structureId, subject: acc.identity, level }).then(() => { const f = live('add-user', addIn); if (f.value.trim() === typed) f.value = ''; }));
    }, admin);
    accSec.appendChild(this.row(addIn, addLvl, addBtn));
    body.appendChild(accSec);

    // FURNISHING — the packs
    // THE GUILD HALL KEEPS ITS OWN FURNISHING (2026-09-25): the console sold it packs the interior never seats (the
    // hall furnishes itself) — SOV for nothing. No packs for a hall.
    if (s.structureType === 'guild_hall') {
      const gSec = this.section('FURNISHING');
      gSec.appendChild(this.text('The guild hall keeps its own furnishing — furnish packs are for houses and civic buildings.', 'var(--ax-muted)'));
      body.appendChild(gSec);
    }
    const fSec = this.section('FURNISHING — the interior is empty until you buy a pack; a bigger pack is a fuller room');
    const cur = furnish ? furnishPack(furnish.pack) : null;
    fSec.appendChild(this.text(cur ? `Furnished with pack ${cur.pack} · ${cur.label} (paid ${furnish!.paidSov.toString()} SOV).` : 'Unfurnished.', cur ? 'var(--ax-good)' : 'var(--ax-muted)'));
    const blocked = s.itemCount > 0;
    if (blocked) fSec.appendChild(this.text(`Remove your ${s.itemCount} placed item${s.itemCount === 1 ? '' : 's'} first — a pack replaces the room's contents.`, 'var(--ax-warn)'));
    // THE LAYOUT PIN (interiors programme, slice C, 2026-10-01): which plan seats this structure's pack. No pin = the
    // classic layout (every pack bought before the pin). A first furnishing takes the current layout; an upgrade keeps
    // the one the room has; Refit moves it — only over EMPTY pack storage (the server's guard; the reason is shown).
    const lay = this.layoutState(structureId, s.structureType, !!cur);
    const counts = this.packCountsFor(s.structureType, this.placed?.interiorId ?? null, lay.buyLayout);
    for (const { pack, items } of counts) {
      const isCur = cur?.pack === pack.pack;
      const line = this.text(`Pack ${pack.pack} · ${pack.label} — ${items < 0 ? '…' : items} piece${items === 1 ? '' : 's'}${pack.floors > 1 ? `, ${Math.min(pack.floors, 3)} floors` : ''} · ${pack.priceSov} SOV${isCur ? ' · current' : ''}`, isCur ? 'var(--ax-good)' : 'var(--ax-soft)'); // THE TOKEN LAW (2026-09-25): was a hex literal
      line.style.minWidth = '300px';
      const buy = this.button(isCur ? 'Bought' : 'Buy', () => call(`Pack ${pack.pack} bought — the room is being furnished.`, () => r.buyFurnishPack({ structureId, pack: pack.pack }).then(() => this.afterBuy(structureId, !cur))), admin && !blocked && !isCur);
      buy.setAttribute('data-k', `pack:${pack.pack}`);
      fSec.appendChild(this.row(line, buy));
    }
    if (cur) {
      const clr = this.button('Clear furnishing (free, no refund)', () => call('Furnishing cleared.', () => r.clearFurnishPack({ structureId })), admin);
      clr.setAttribute('data-k', 'pack:clear');
      fSec.appendChild(this.row(clr));
    }
    // The pin, and Refit.
    const pinLine = this.text(lay.line, 'var(--ax-soft)');
    pinLine.setAttribute('data-k', 'layout:pin');
    fSec.appendChild(pinLine);
    // THE DRIFT POLICY (C2): ONE note, and Refit. Revisions are bumped once per release; inside one, a pinned room that
    // today's plan no longer matches stays as pinned where a tradition still reproduces it, else is drawn as today's plan.
    if (lay.drift) { const w = this.text(lay.driftNote, 'var(--ax-warn)'); w.setAttribute('data-k', 'layout:drift'); fSec.appendChild(w); }
    if (lay.leverNote) { const w = this.text(lay.leverNote, 'var(--ax-warn)'); w.setAttribute('data-k', 'layout:lever'); fSec.appendChild(w); }
    if (cur && lay.pinOn) {
      const refuse = lay.refitRefusal;
      if (refuse) { const w = this.text(`Refit is not possible right now: ${refuse}`, 'var(--ax-muted)'); w.setAttribute('data-k', 'layout:refusal'); fSec.appendChild(w); }
      // A Refit of a DRIFTED room clears the digest: the visit re-plans the room as today's plan (today's tradition) and
      // the owner's client stamps that — never the pinned plan it is leaving.
      const digest = (): string => (!lay.drift && lay.currentDigest && /^[0-9a-f]{32}$/.test(lay.currentDigest) ? lay.currentDigest : '');
      const rr = r as unknown as { refitStructureLayout(a: { structureId: bigint; layoutRevision: number; digest: string }): Promise<void> };
      const rowEls: HTMLElement[] = [];
      if (lay.layout !== lay.current || lay.drift) {
        const b1 = this.button(lay.layout === lay.current ? 'Refit — move this room to today\'s plan' : 'Refit to the current layout', () => call('Refitted — the room is being re-seated.', () => rr.refitStructureLayout({ structureId, layoutRevision: lay.current, digest: lay.layout === lay.current ? digest() : '' })), isOwner && !refuse);
        b1.setAttribute('data-k', 'layout:refit');
        rowEls.push(b1);
      }
      if (lay.layout !== 0) {
        const b0 = this.button('Return to the classic layout', () => call('The classic layout is back — the room is being re-seated.', () => rr.refitStructureLayout({ structureId, layoutRevision: 0, digest: '' })), isOwner && !refuse);
        b0.setAttribute('data-k', 'layout:classic');
        rowEls.push(b0);
      }
      if (rowEls.length) fSec.appendChild(this.row(...rowEls));
      if (!isOwner) fSec.appendChild(this.text('Only the owner refits a structure.', 'var(--ax-muted)'));
    }
    // What a layout no longer seats: always reachable (nothing a player stored is ever orphaned by a layout change).
    if (isOwner && lay.unseated.length) {
      const head = this.text('STORAGE NOT SEATED IN THIS ROOM — a layout change left these without a piece; take the contents out here:', 'var(--ax-warn)');
      head.setAttribute('data-k', 'layout:unseated');
      fSec.appendChild(head);
      for (const u of lay.unseated) {
        const label = this.text(`${FURNITURE[u.kind]?.name ?? u.kind.replace(/_/g, ' ')} · ${u.what}`, 'var(--ax-text)'); label.style.minWidth = '300px';
        const open = this.button('Open', () => { void this.app.getModule<FurnitureUseModule>('FurnitureUse')?.openUnseated(structureId, u.key, u.kind); }, true);
        const urow = this.row(label, open);
        urow.setAttribute('data-row', `unseated:${u.key}`);
        fSec.appendChild(urow);
      }
    }
    if (s.structureType !== 'guild_hall') body.appendChild(fSec); // the hall's own note stands in its place (above)

    // RENTALS (the interiors programme, slice R, 2026-10-01): let a room — every room the planner drew here, its
    // state (not listed / to let / let to <tenant>), list / re-price / withdraw / notice. modules/rentals/console_rentals.ts.
    const rentSec = buildRentalsSection({
      structureId, structureType: s.structureType, interiorId: this.placed?.interiorId ?? atlas.interiorId, admin, isOwner, furnished: !!furnish,
      section: (t) => this.section(t), row: (...els) => this.row(...els), text: (t, c) => this.text(t, c),
      input: (v, ph, m) => this.input(v, ph, m), button: (l, f, e) => this.button(l, f, e), live, call, nameOf,
    });
    if (rentSec) body.appendChild(rentSec);

    // MAINTENANCE
    const mSec = this.section('MAINTENANCE');
    mSec.appendChild(this.text(`Pool ${s.maintPool.toString()} h banked · condition ${Math.round(s.condition / 10)} % · items ${s.itemCount} / ${s.itemCap}${s.cityId !== 0n ? ` · in city #${s.cityId}` : ''}`, 'var(--ax-soft)')); // THE TOKEN LAW (2026-09-25): was a hex literal
    const payIn = this.input('', 'SOV to bank', 8); payIn.type = 'number'; payIn.min = '1'; payIn.style.width = '120px';
    payIn.setAttribute('data-k', 'pay');
    const payBtn = this.button('Pay maintenance', () => {
      const n = Math.floor(Number(live('pay', payIn).value));
      if (!(n > 0)) { bus.emit('atlas:toast', { text: 'Enter an amount.', kind: 'error' }); return; }
      call(`Banked ${n}.`, () => r.payMaintenance({ structureId, amount: BigInt(n) }).then(() => { live('pay', payIn).value = ''; })); // cleared once paid (2026-09-25)
    }, true);
    mSec.appendChild(this.row(payIn, payBtn));
    body.appendChild(mSec);

    // REDEED
    const rSec = this.section('REDEED — pack the structure back into a deed in your inventory');
    // THE SERVER'S CITY RULE (2026-09-25): only a CIVIC building or the owner's REGISTERED RESIDENCE is held by its city
    // — a plain house the pulse adopted inside a border is released as it is packed. The console refused every house
    // in a city ("leave the city first", a path that never existed) after the server stopped doing so.
    const cdb = atlas.conn!.db as unknown as {
      civicStructure?: { structureId: { find(id: bigint): unknown } };
      citizen?: { iter(): Iterable<{ identity: Identity; residenceStructureId: bigint }> };
    };
    const civicHeld = s.cityId !== 0n && !!cdb.civicStructure?.structureId.find(structureId);
    let residenceHeld = false;
    if (s.cityId !== 0n && me) for (const z of cdb.citizen?.iter() ?? []) if (z.identity.isEqual(me) && z.residenceStructureId === structureId) { residenceHeld = true; break; }
    const cityHeld = civicHeld || residenceHeld;
    if (civicHeld) rSec.appendChild(this.text('A civic building is taken down by its city — it cannot be redeeded.', 'var(--ax-warn)'));
    else if (residenceHeld) rSec.appendChild(this.text('This is your registered residence — declare another residence first.', 'var(--ax-warn)'));
    rSec.appendChild(this.text('Packing up spends banked maintenance (the server names the amount if the pool is short) — the deed keeps the rest.', 'var(--ax-muted)'));
    const armed = Date.now() - this.armedAt < REDEED_ARM_MS;
    const redeedBtn = this.button(armed ? 'CONFIRM — pack it up now' : 'Pack into a deed…', () => {
      if (!(Date.now() - this.armedAt < REDEED_ARM_MS)) {
        r.redeedStructure({ structureId, confirm: false })
          .then(() => { this.armedAt = Date.now(); bus.emit('atlas:toast', { text: 'Confirm within 30 s to pack it up.', kind: 'info' }); this.render(); })
          .catch((err: Error) => bus.emit('atlas:toast', { text: String(err?.message ?? err).replace(/^SenderError:\s*/, '').slice(0, 160), kind: 'error' }));
        return;
      }
      r.redeedStructure({ structureId, confirm: true })
        .then(() => {
          this.armedAt = 0;
          bus.emit('atlas:toast', { text: `${shown} packed into a deed — it is in your inventory.`, kind: 'success' });
          sfx('sfx_structure_place', { gain: 0.4 });
          this.win?.setOpen(false);
          bus.emit('atlas:interior-exit', { reason: 'redeeded' }); // the shell goes with the exterior
        })
        .catch((err: Error) => bus.emit('atlas:toast', { text: String(err?.message ?? err).replace(/^SenderError:\s*/, '').slice(0, 160), kind: 'error' }));
    }, isOwner && !cityHeld);
    redeedBtn.setAttribute('data-k', 'redeed');
    if (armed) redeedBtn.style.borderColor = 'var(--ax-bad)';
    rSec.appendChild(this.row(redeedBtn));
    body.appendChild(rSec);
  }

  /** What each pack would seat in THIS room, at the layout a purchase would give it (`buyLayout`): 0 = the classic plan
   *  (`packItemCounts`, as before 2026-10-01); ≥ 1 = the planner's room at that revision — the active descriptor when
   *  the room is already planned there, else a plan asked for off the frame (`-1` pieces = not landed yet). */
  private packCountsFor(structureType: string, interiorId: string | null, buyLayout = 0): Array<{ pack: FurnishPack; items: number }> {
    const b = interiorId ? enterables.get(interiorId) : undefined;
    if (!b) return FURNISH_PACKS.map((pack) => ({ pack, items: 0 }));
    const spec = interiorSpec(b.w, b.h, b.d, b.key);
    const purpose = resolvePurpose(b.id, `deed:${structureType}`).purpose;
    if (buyLayout <= 0) return packItemCounts(purpose, { hw: spec.hw, hd: spec.hd, stair: spec.stair }, spec.floors);
    const act = activeInterior();
    if (act && act.id === b.id && act.descriptor && act.layout === buyLayout) return packCountsOf(act.descriptor);
    // the LOOK's tradition, as the visit plans it (C2 item 6) — the console stands inside the room it prices
    const input = planInputFor(b, purpose, buyLayout, act && act.id === b.id ? act.tradition ?? undefined : undefined);
    const hit = planCachePeek(input);
    if (hit) return packCountsOf(hit.descriptor);
    void planRoom(input, spec).then(() => this.scheduleRender()); // lands in the plan cache; the window re-renders
    return FURNISH_PACKS.map((pack) => ({ pack, items: -1 }));
  }

  /** The layout pin as the Furnishing section shows it: the line, the layout a purchase would get, why a Refit would be
   *  refused (the server's own rules, read from the rows the owner holds), and the storage no piece of the room seats. */
  private layoutState(structureId: bigint, structureType: string, furnished: boolean): {
    layout: number; pin: LayoutPin | null; pinOn: boolean; buyLayout: number; line: string; drift: boolean; driftNote: string; leverNote: string;
    /** THE SHARD'S current layout (the server's `layoutRevisionNow`): `CURRENT_LAYOUT_REVISION`, or the rooms revision
     *  while `interior_storeys_v1` is off — a Refit offered past it is refused ("The storeys revision is switched off"). */
    current: number;
    refitRefusal: string; currentDigest: string; unseated: Array<{ key: string; kind: string; what: string }>;
  } {
    const conn = atlas.conn;
    interface PinDb {
      structureLayout?: { structureId: { find(id: bigint): { layoutRevision: number; digest: string } | undefined } };
      platformFlag?: { key: { find(k: string): { enabled: boolean } | undefined } };
      structureContainer?: { iter(): Iterable<{ containerId: bigint; structureId: bigint; key: string; kind: string }> };
      containerItem?: { iter(): Iterable<{ containerId: bigint; qty: number }> };
      structurePlanter?: { iter(): Iterable<{ structureId: bigint; key: string; name: string }> };
    }
    const db: PinDb = conn ? (conn.db as unknown as PinDb) : ({} as PinDb); // THE TS2589 TRAP: never `(conn?.db ?? {}) as unknown as …`
    let pin: LayoutPin | null = null, pinOn = true;
    try { const row = db.structureLayout?.structureId.find(structureId); pin = row ? { layoutRevision: row.layoutRevision, digest: row.digest } : null; } catch { pin = null; }
    try { pinOn = db.platformFlag?.key.find(LAYOUT_PIN_FLAG)?.enabled !== false; } catch { pinOn = true; }
    let storeysOn = true;
    try { storeysOn = db.platformFlag?.key.find(STOREYS_FLAG)?.enabled !== false; } catch { storeysOn = true; } // absent = ON (the server's flagOn default)
    const current = storeysOn ? CURRENT_LAYOUT_REVISION : Math.min(CURRENT_LAYOUT_REVISION, ROOMS_LAYOUT_REVISION);
    const lever = interiorPlannerEnabled();
    const layout = Math.max(0, Math.min(CURRENT_LAYOUT_REVISION, pin?.layoutRevision ?? 0));
    // A FIRST furnishing is stamped with the current layout (flag on); an upgrade keeps the layout the room has. With
    // the planner switched off on this client the console re-pins a new purchase to the classic layout (afterBuy).
    const buyLayout = furnished ? (lever ? layout : 0) : (pinOn && lever ? current : 0);
    const act = activeInterior();
    const mine = act && act.id === `deed:${structureId}` ? act : null;
    const drift = !!mine && mine.digestDrift;
    const line = !furnished ? (buyLayout >= 1 ? `Layout: a new furnishing takes the current layout (revision ${current}).` : 'Layout: a new furnishing takes the classic layout.')
      : !pin ? 'Layout: classic (no pin — furnished before the planner; its storage keeps its places).'
      : pin.layoutRevision === 0 ? 'Layout: classic (pinned).'
      : `Layout: revision ${pin.layoutRevision}${pin.layoutRevision > CURRENT_LAYOUT_REVISION ? ' (newer than this client knows)' : ''} · digest ${pin.digest ? pin.digest.slice(0, 12) + '…' : 'not recorded yet'}`;
    // ONE drift note (the line above carries no second flag). It says which kind: a tradition change keeps the pinned
    // room exactly; a planner / kit / catalog change inside one revision draws today's plan at that revision.
    const keptTradition = !!mine && !!mine.descriptor && !!pin && mine.descriptor.digest === pin.digest && !!mine.plannedTradition && !!mine.tradition && mine.plannedTradition !== mine.tradition;
    const driftNote = !drift ? ''
      : keptTradition ? `This room is pinned in the ${mine!.plannedTradition} tradition and its city now builds in the ${mine!.tradition} tradition. It stays exactly as pinned, storage and all, until you Refit: with the pack's storage empty, Refit moves it to today's plan.`
      : 'This room no longer matches the plan it was pinned to (the planner or the furniture catalog changed inside this layout revision — revisions themselves change once per release). It is drawn as today\'s plan; nothing you stored is lost — a container whose piece is gone is listed below and opens from there. With the pack\'s storage empty, Refit re-pins the room.';
    const leverNote = !lever && layout >= 1 ? 'The interior planner is switched off on this client: this room is drawn in the classic layout, so its pack storage is reached from the list below, not from the furniture.' : '';
    // What the room seats (storage keys), and what the structure holds that it does not.
    let seated = new Set<string>();
    if (mine?.view && mine.route === 'descriptor') seated = mine.view.storageKeys;
    else if (mine && mine.keyedOk && mine.route === 'pack' && mine.pack) {
      const b = enterables.get(mine.id), fp = furnishPack(mine.pack);
      if (b && fp) {
        const spec = interiorSpec(b.w, b.h, b.d, b.key);
        packPlan(fp, resolvePurpose(b.id, `deed:${structureType}`).purpose, { hw: spec.hw, hd: spec.hd, stair: spec.stair }, spec.floors)
          .forEach((slots, f) => slots.forEach((sl, i) => seated.add(`pack:${f}:${i}:${sl.key}`)));
      }
    }
    const held = new Map<bigint, number>();
    try { for (const e of db.containerItem?.iter() ?? []) held.set(e.containerId, (held.get(e.containerId) ?? 0) + 1); } catch { /* table mid-churn */ }
    const unseated: Array<{ key: string; kind: string; what: string }> = [];
    let packHoldings = 0, packPlanters = 0;
    try {
      const conts = [...(db.structureContainer?.iter() ?? [])].filter((c) => c.structureId === structureId && c.key.startsWith('pack:'));
      const off = new Set(mine ? unseatedPackKeys(conts.map((c) => c.key), seated) : []);
      for (const c of conts) {
        const n = held.get(c.containerId) ?? 0;
        packHoldings += n;
        if (n > 0 && off.has(c.key)) unseated.push({ key: c.key, kind: c.kind, what: `${n} stored` });
      }
      for (const pl of db.structurePlanter?.iter() ?? []) {
        if (pl.structureId !== structureId || !pl.key.startsWith('pack:')) continue;
        packPlanters++;
        if (mine && !seated.has(pl.key)) unseated.push({ key: pl.key, kind: pl.key.split(':').pop() ?? 'planter_hydro', what: `${pl.name} growing` });
      }
    } catch { /* tables mid-churn */ }
    const refitRefusal = !furnished ? 'buy a furnish pack first.'
      : packHoldings > 0 ? `empty the pack's storage first (${packHoldings} stored in the pack's furniture).`
      : packPlanters > 0 ? 'harvest the pack\'s planter first.' : '';
    return { layout, pin, pinOn, buyLayout, line, drift, driftNote, leverNote, current, refitRefusal, currentDigest: mine?.descriptor && mine.layout === current ? mine.descriptor.digest : '', unseated };
  }

  /** After a purchase: with the planner switched off on this client, a FIRST furnishing (which the server stamped with
   *  the current layout) is re-pinned to the classic layout at once — the storage is empty, so the Refit is allowed —
   *  and the lever stays a whole rollback: the room this client draws is the room its storage keys name. */
  private async afterBuy(structureId: bigint, first: boolean): Promise<void> {
    if (!first || interiorPlannerEnabled()) return;
    const r = atlas.conn?.reducers as unknown as { refitStructureLayout?(a: { structureId: bigint; layoutRevision: number; digest: string }): Promise<void> } | undefined;
    try { await r?.refitStructureLayout?.({ structureId, layoutRevision: 0, digest: '' }); } catch { /* the flag is off (no pin was stamped) — the room is classic already */ }
  }

  // ─── DOM helpers ────────────────────────────────────────────────────────────

  private heading(text: string): HTMLElement { const d = document.createElement('div'); d.textContent = text; d.style.cssText = 'font-weight:600;color:var(--ax-hi);letter-spacing:.03em'; return d; }
  private section(title: string): HTMLElement {
    const d = document.createElement('div'); d.style.cssText = 'display:flex;flex-direction:column;gap:6px;padding:8px 10px;border:1px solid var(--ax-line);border-radius:var(--ax-radius);background:var(--ax-bg2)';
    const t = document.createElement('div'); t.textContent = title; t.style.cssText = 'font-size:11px;color:var(--ax-hi);letter-spacing:.06em'; d.appendChild(t); return d;
  }
  private row(...els: HTMLElement[]): HTMLElement { const d = document.createElement('div'); d.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap'; for (const e of els) d.appendChild(e); return d; }
  private text(text: string, color: string): HTMLElement { const d = document.createElement('div'); d.textContent = text; d.style.color = color; return d; }
  private input(value: string, placeholder: string, max: number): HTMLInputElement {
    const i = document.createElement('input'); i.type = 'text'; i.value = value; i.placeholder = placeholder; i.maxLength = max;
    i.style.cssText = 'flex:1;min-width:160px;padding:6px 8px;border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:var(--ax-bg2);color:var(--ax-text);font:13px var(--ax-font)'; return i;
  }
  private select(opts: Array<readonly [string, string]>, value: string): HTMLSelectElement {
    const s = document.createElement('select'); s.style.cssText = 'padding:6px 8px;border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:var(--ax-bg2);color:var(--ax-text);font:13px var(--ax-font)';
    for (const [v, label] of opts) { const o = document.createElement('option'); o.value = v; o.textContent = label; if (v === value) o.selected = true; s.appendChild(o); }
    return s;
  }
  private button(label: string, onClick: () => void, enabled: boolean): HTMLButtonElement {
    const b = document.createElement('button'); b.textContent = label; b.disabled = !enabled;
    b.style.cssText = 'padding:6px 12px;border-radius:var(--ax-radius);border:1px solid var(--ax-line2);background:var(--ax-bg3);color:var(--ax-text);font:13px var(--ax-font);cursor:pointer';
    if (!enabled) b.style.opacity = '0.5';
    b.addEventListener('click', onClick); return b;
  }

  /** Probe lever. */
  private diag(): unknown {
    const p = this.placed;
    return {
      interior: atlas.interiorId,
      structureId: p ? p.structureId.toString() : null,
      consoleAt: p ? [p.x, p.y, p.z] : null,
      dressed: !!p && p.group.children.length > 1 && !p.group.children[0].visible,
      near: this.near,
      promptShown: !!this.prompt && this.prompt.style.display !== 'none',
      open: !!this.win?.isOpen(),
      open_: () => this.open(),
      close: () => this.win?.setOpen(false),
      /** Stand a metre in front of the console (a probe; the real game walks). The SERVER row moves too —
       *  a client-only hop past the reconcile window is snapped back (the debug reducer needs the dev flag). */
      standAt: async () => {
        if (!p) return false;
        const b = enterables.get(p.interiorId);
        const yaw = b ? b.yaw : 0;
        // One metre toward the room (the console faces −Z of the building frame; "toward the room" is −local z).
        const x = p.x - Math.sin(yaw) * 1.0, z = p.z - Math.cos(yaw) * 1.0;
        try { await (atlas.conn!.reducers as unknown as { debugTeleport(a: { posX: number; posZ: number }): Promise<void> }).debugTeleport({ posX: x, posZ: z }); } catch { /* no dev flag: the local hop alone */ }
        atlas.localX = x; atlas.localZ = z;
        return true;
      },
      setName: (name: string) => (atlas.conn!.reducers as unknown as { setStructureName(a: { structureId: bigint; name: string }): Promise<void> }).setStructureName({ structureId: p!.structureId, name }),
      setEntry: (entry: string) => (atlas.conn!.reducers as unknown as { setStructureEntry(a: { structureId: bigint; entry: string }): Promise<void> }).setStructureEntry({ structureId: p!.structureId, entry }),
      redeed: async (): Promise<{ ok: boolean; error: string }> => {
        const r = atlas.conn!.reducers as unknown as { redeedStructure(a: { structureId: bigint; confirm: boolean }): Promise<void> };
        const id = p!.structureId;
        try {
          await r.redeedStructure({ structureId: id, confirm: false });
          await r.redeedStructure({ structureId: id, confirm: true });
        } catch (e) { const error = String((e as Error)?.message ?? e); bus.emit('atlas:toast', { text: error.replace(/^SenderError:\s*/, ''), kind: 'error' }); return { ok: false, error }; }
        this.win?.setOpen(false);
        bus.emit('atlas:toast', { text: 'Packed into a deed — it is in your inventory.', kind: 'success' }); // the window's own success line
        bus.emit('atlas:interior-exit', { reason: 'redeeded' });
        return { ok: true, error: '' };
      },
      buyPack: (pack: number) => (atlas.conn!.reducers as unknown as { buyFurnishPack(a: { structureId: bigint; pack: number }): Promise<void> }).buyFurnishPack({ structureId: p!.structureId, pack }),
      clearPack: () => (atlas.conn!.reducers as unknown as { clearFurnishPack(a: { structureId: bigint }): Promise<void> }).clearFurnishPack({ structureId: p!.structureId }),
      // (the levers read the rows of the structure the console STANDS in — `rows()` alone follows the open window, and
      //  a probe that never opened it read "not furnished" for a furnished house, 2026-10-01)
      packCounts: (layout?: number) => (p ? this.packCountsFor(this.rows(p.structureId).s?.structureType ?? 'small_house', p.interiorId, layout ?? this.layoutState(p.structureId, this.rows(p.structureId).s?.structureType ?? 'small_house', !!this.rows(p.structureId).furnish).buyLayout).map((c) => ({ pack: c.pack.pack, items: c.items, price: c.pack.priceSov })) : []),
      layout: () => (p ? (() => { const l = this.layoutState(p.structureId, this.rows(p.structureId).s?.structureType ?? 'small_house', !!this.rows(p.structureId).furnish); return { layout: l.layout, pin: l.pin, pinOn: l.pinOn, buyLayout: l.buyLayout, line: l.line, drift: l.drift, driftNote: l.driftNote, leverNote: l.leverNote, refitRefusal: l.refitRefusal, unseated: l.unseated }; })() : null),
      refit: (layoutRevision: number, digest = '') => (atlas.conn!.reducers as unknown as { refitStructureLayout(a: { structureId: bigint; layoutRevision: number; digest: string }): Promise<void> }).refitStructureLayout({ structureId: p!.structureId, layoutRevision, digest }),
      pay: (amount: number) => (atlas.conn!.reducers as unknown as { payMaintenance(a: { structureId: bigint; amount: bigint }): Promise<void> }).payMaintenance({ structureId: p!.structureId, amount: BigInt(Math.floor(amount)) }),
    };
  }

  dispose(): void {
    this.teardown();
    window.removeEventListener('keydown', this.onKey);
    this.prompt?.remove();
    this.prompt = null;
  }
}
